/* ModuleGatewayGrid — cuadrícula de 8 botones grandes con re-autenticación.
   Cada botón abre un modal que pide usuario+contraseña antes de entrar al módulo.
   Botones con candado son visibles pero no navegables (toast de aviso).
   Diseño solicitado 11-jun-2026. NO modifica módulos existentes. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";

const MODULOS = [
  {
    id: "combustible_admin",
    nombre: "Control de Combustible",
    sub: "Administrativo",
    icon: "fa-chart-column",
    color: "#1a6e3c",
    roles_permitidos: ["coordinadora_administrativa", "capturista_combustible", "jefa_capturistas", "admin"],
    to: "/combustible",
  },
  // Carga de gasolina / Mi Combustible — bloqueado 20-jun-2026 (choferes vía WhatsApp Bot)
  { id: "mi_combustible_lock", nombre: "Carga de gasolina",  sub: "Próximamente", icon: "fa-gas-pump",      color: "#888", bloqueado: true },
  // Pipas — bloqueado 20-jun-2026 (choferes vía WhatsApp Bot)
  { id: "pipas_lock",          nombre: "Pipas",              sub: "Próximamente", icon: "fa-truck-droplet", color: "#888", bloqueado: true },
  // Áreas Contratadas — bloqueado 20-jun-2026
  { id: "areas_lock",          nombre: "Áreas Contratadas",  sub: "Próximamente", icon: "fa-leaf",          color: "#888", bloqueado: true },
  {
    id: "estatus_flotilla",
    nombre: "Estatus de Flotilla",
    sub: "Intervenciones · Auditoría · Choferes · Reporte",
    icon: "fa-gauge-high",
    color: "#0369A1",
    roles_permitidos: ["admin"],
    to: "/supervisor",
  },
  {
    id: "supervisor",
    nombre: "Supervisor",
    sub: "Panel de supervisión",
    icon: "fa-user-tie",
    color: "#7F77DD",
    // 12-jun-2026: revertido — sup_general_pipas debe ver Supervisor de nuevo.
    // 20-jun-2026: + coordinador_pipas (Rubén) acceso ampliado
    roles_permitidos: ["supervisor_general_pipas", "supervisor_pipas", "coordinador_pipas"],
    to: "/bandeja-reportes-ac",
  },
  { id: "vialidades_lock", nombre: "Vialidades",            sub: "Próximamente", icon: "fa-road",          color: "#888", bloqueado: true },
  { id: "maquinaria_lock", nombre: "Maquinaria y Derribos", sub: "Próximamente", icon: "fa-truck-monster", color: "#888", bloqueado: true },
  {
    id: "control_vehicular",
    nombre: "Control Vehicular",
    sub: "Catálogo y resguardos",
    icon: "fa-car",
    color: "#4C1D95",
    roles_permitidos: ["admin", "control_vehicular"],
    to: "/admin/control-vehicular",
  },
];

function visibleParaRol(modulo, rol) {
  if (modulo.bloqueado) return true;                       // candados visibles para todos
  if (!rol) return false;
  return modulo.roles_permitidos.includes(rol);
}

export default function ModuleGatewayGrid() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [moduloAuth, setModuloAuth] = useState(null);

  const visibles = MODULOS.filter(m => visibleParaRol(m, user?.rol));

  const handleClick = (m) => {
    if (m.bloqueado) {
      toast.info("Módulo en desarrollo");
      return;
    }
    // Si ya hay sesión activa Y el rol del usuario tiene acceso al módulo,
    // entrar directo sin re-autenticación (decisión Octavio 11-jun-2026).
    if (user && m.roles_permitidos?.includes(user.rol)) {
      navigate(m.to);
      return;
    }
    // En caso contrario (sin sesión o rol no permitido), abrir modal de auth.
    setModuloAuth(m);
  };

  return (
    <section className="mb-8" data-testid="module-gateway">
      <div className="mb-4">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-800">
          ¿A qué módulo vas a acceder hoy?
        </h2>
        <p className="text-slate-500 text-sm mt-1">
          Selecciona el módulo y confirma tu identidad para entrar.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {visibles.map(m => (
          <BotonModulo key={m.id} modulo={m} onClick={() => handleClick(m)} />
        ))}
      </div>

      {moduloAuth && (
        <ModuleAuthModal
          modulo={moduloAuth}
          onClose={() => setModuloAuth(null)}
        />
      )}
    </section>
  );
}

function BotonModulo({ modulo, onClick }) {
  const m = modulo;
  const disabled = m.bloqueado;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={false}  /* dejamos clickable para mostrar el toast de aviso */
      data-testid={`gateway-${m.id}`}
      aria-label={m.nombre}
      className="relative rounded-2xl flex flex-col items-center justify-center text-white p-4 transition-transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-blue-300"
      style={{
        background: m.color,
        minHeight: 140,
        minWidth: 120,
        opacity: disabled ? 0.7 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        border: 0,
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      }}
    >
      {disabled && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute", top: 8, right: 10, fontSize: 20,
            background: "rgba(0,0,0,0.25)", borderRadius: 999,
            padding: "2px 8px", color: "#FFFBEB",
          }}
        >
          <i className="fa-solid fa-lock"/>
        </span>
      )}
      <i className={`fa-solid ${m.icon}`} style={{ fontSize: 38, marginBottom: 10, opacity: 0.95 }}/>
      <div className="text-sm sm:text-base font-bold leading-tight text-center px-1">
        {m.nombre}
      </div>
      <div className="text-[0.7rem] sm:text-xs opacity-80 mt-1 text-center">
        {m.sub}
      </div>
    </button>
  );
}

/* ──────────────────────────────────────────────────────────── */
/*   Modal de re-autenticación por módulo                       */
/* ──────────────────────────────────────────────────────────── */
function ModuleAuthModal({ modulo, onClose }) {
  const { loginWeb } = useAuth();
  const navigate = useNavigate();
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!usuario.trim() || !password) {
      setError("Usuario y contraseña son obligatorios");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const u = await loginWeb(usuario.trim(), password);
      // Verificar acceso al módulo
      if (!modulo.roles_permitidos.includes(u.rol)) {
        setError("No tienes acceso a este módulo");
        return;
      }
      // Navegar al módulo (Protected hará redirects adicionales según rol — Luisa→/capturista-ac, etc.)
      navigate(modulo.to);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 400) {
        setError("Usuario o contraseña incorrectos");
      } else {
        setError("Error: " + (err.response?.data?.detail || err.message));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      data-testid="modal-auth-gateway"
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.75)",
        zIndex: 95, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        style={{
          background: "white", borderRadius: 16, maxWidth: 420, width: "100%",
          padding: 20, boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
        }}
      >
        <div className="flex items-start gap-3 mb-4">
          <div
            style={{
              background: modulo.color, color: "white",
              width: 48, height: 48, borderRadius: 12,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <i className={`fa-solid ${modulo.icon}`} style={{ fontSize: 22 }}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-lg leading-tight">{modulo.nombre}</div>
            <div className="text-xs text-slate-500">{modulo.sub}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            data-testid="btn-cerrar-auth"
            style={{ background: "transparent", border: 0, fontSize: 22, color: "#64748B" }}
          >
            <i className="fa-solid fa-xmark"/>
          </button>
        </div>

        <div className="text-sm text-slate-600 mb-3">
          Confirma tu identidad para entrar.
        </div>

        <label className="block text-xs uppercase font-bold text-slate-500 mb-1">Usuario</label>
        <input
          type="text"
          value={usuario}
          onChange={(e) => setUsuario(e.target.value)}
          autoFocus
          autoComplete="username"
          data-testid="inp-auth-usuario"
          style={{
            width: "100%", border: "1px solid #cbd5e1", borderRadius: 8,
            padding: "10px 12px", minHeight: 48, fontSize: 16, marginBottom: 12,
          }}
        />

        <label className="block text-xs uppercase font-bold text-slate-500 mb-1">Contraseña</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          data-testid="inp-auth-password"
          style={{
            width: "100%", border: "1px solid #cbd5e1", borderRadius: 8,
            padding: "10px 12px", minHeight: 48, fontSize: 16, marginBottom: 12,
          }}
        />

        {error && (
          <div
            data-testid="auth-error"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3"
          >
            <i className="fa-solid fa-triangle-exclamation mr-1"/>{error}
          </div>
        )}

        <div className="flex gap-2 mt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="btn-cancelar-auth"
            className="flex-1 rounded-lg py-3 font-bold text-slate-700"
            style={{ background: "#F1F5F9", border: 0, minHeight: 48 }}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="btn-entrar-auth"
            className="flex-1 rounded-lg py-3 font-bold text-white"
            style={{ background: modulo.color, border: 0, minHeight: 48 }}
          >
            <i className="fa-solid fa-right-to-bracket mr-1"/>{busy ? "Verificando…" : "Entrar"}
          </button>
        </div>
      </form>
    </div>
  );
}
